/**
 * Languages component - Toggle between language codes (text-only)
 */

import type { Language } from "@/types/user-types.js";
import "./Languages.scss";

export interface LanguagesConfig {
  readonly selected?: Language;
  readonly onSelectionChange?: (language: Language) => void;
  readonly languages?: readonly Language[];
}

export interface LanguagesAPI {
  readonly getElement: () => HTMLElement;
  readonly setSelected: (language: Language) => void;
  readonly getSelected: () => Language | null;
  readonly destroy: () => void;
}

/**
 * Creates a Languages component with mutually exclusive selection
 */
export const createLanguages = (config: LanguagesConfig = {}): LanguagesAPI => {
  const defaultLanguages: readonly Language[] = [
    "en",
    "es",
    "fr",
    "de",
    "pt",
    "it",
    "ja",
    "ko",
    "zh",
  ] as const;

  const {
    selected = "en",
    onSelectionChange,
    languages = defaultLanguages,
  } = config;

  let currentSelected: Language | null = selected;
  let isDestroyed = false;

  // Container
  const container = document.createElement("div");
  container.className = "languages";

  // Button map for quick updates
  const buttonByLanguage: Map<Language, HTMLButtonElement> = new Map();

  const updateSelection = (): void => {
    if (isDestroyed) return;
    buttonByLanguage.forEach((button, lang) => {
      if (currentSelected === lang) {
        button.classList.add("languages__button--selected");
      } else {
        button.classList.remove("languages__button--selected");
      }
    });
  };

  const handleClick = (next: Language): void => {
    if (isDestroyed || next === currentSelected) return;
    currentSelected = next;
    updateSelection();
    onSelectionChange?.(next);
  };

  const initButtons = (): void => {
    languages.forEach((lang) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "languages__button";
      button.textContent = lang.toUpperCase();
      if (currentSelected === lang) {
        button.classList.add("languages__button--selected");
      }
      button.addEventListener("click", () => handleClick(lang));
      buttonByLanguage.set(lang, button);
      container.appendChild(button);
    });
  };

  // Initialize
  initButtons();

  const api: LanguagesAPI = {
    getElement: () => container,
    setSelected: (language: Language) => {
      if (isDestroyed) return;
      currentSelected = language;
      updateSelection();
    },
    getSelected: () => currentSelected,
    destroy: () => {
      if (isDestroyed) return;
      isDestroyed = true;
      // Remove events and clear DOM
      buttonByLanguage.forEach((button) => {
        button.replaceWith(button.cloneNode(true));
      });
      buttonByLanguage.clear();
      container.remove();
    },
  };

  return api;
};

export const createLanguagesInContainer = (
  container: HTMLElement,
  config: LanguagesConfig
): LanguagesAPI => {
  const languages = createLanguages(config);
  container.appendChild(languages.getElement());
  return languages;
};
